import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Store, Plus, RefreshCw, Trash2, ShieldCheck, ShieldAlert, Sparkles } from "lucide-react";
import { COUNTRIES, defaultLangForCountry, countryLabel } from "@/constants/countries";
import RequiredFieldDot from "@/components/form/RequiredFieldDot";

const VENDOR_TYPES = [
  "sport_equipment", "physiotherapy", "hotel", "transport",
  "training_camp", "coaching", "medical", "marketing",
  "facilities", "insurance", "other",
];
const VERIFICATION = ["unverified", "documents_pending", "internal_verified", "rejected"];
const PILOT_CATEGORIES = ["equipment", "training", "hotel", "transport"];

const VERIFICATION_PILL = {
  unverified: "bg-slate-100 text-slate-700",
  documents_pending: "bg-amber-100 text-amber-900",
  internal_verified: "bg-emerald-100 text-emerald-900",
  rejected: "bg-rose-100 text-rose-900",
};

const blankVendor = {
  vendor_name: "", vendor_type: "sport_equipment", country: "ES", city: "",
  language: "es", service_categories: ["equipment"], verified_status: "internal_verified",
  is_active: true, contact_email: "",
};

const AdminVendors = () => {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [filterCountry, setFilterCountry] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState(blankVendor);
  const [editingId, setEditingId] = useState(null);
  // P11-v2 — Promote dialog state.
  const [promoteVendor, setPromoteVendor] = useState(null);
  const [promoteToken, setPromoteToken] = useState("");
  const [promoteReason, setPromoteReason] = useState("");
  const [promoteTokenExpires, setPromoteTokenExpires] = useState(null);
  const [promoting, setPromoting] = useState(false);
  // Synchronous re-entry guard — React state updates are async so a fast
  // double-click can fire two POSTs before `promoting` flips. The ref
  // closes that window deterministically.
  const promotingRef = useRef(false);

  const fetchList = useCallback(async () => {
    try {
      const params = filterCountry ? `?country=${filterCountry}` : "";
      const r = await api.get(`/admin/vendors${params}`);
      setRows(r.data?.vendors || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminVendors.toasts.failed")));
    }
  }, [filterCountry, t]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openNew = () => { setForm(blankVendor); setEditingId(null); setOpenCreate(true); };
  const openEdit = (v) => { setForm({ ...v }); setEditingId(v.id); setOpenCreate(true); };

  const save = async () => {
    try {
      const payload = {
        vendor_name: form.vendor_name,
        vendor_type: form.vendor_type,
        country: form.country,
        city: form.city || null,
        language: form.language || null,
        service_categories: form.service_categories || [],
        verified_status: form.verified_status,
        is_active: form.is_active,
        contact_email: form.contact_email || null,
      };
      if (editingId) {
        await api.put(`/admin/vendors/${editingId}`, payload);
        toast.success(t("adminVendors.toasts.updated"));
      } else {
        await api.post("/admin/vendors", payload);
        toast.success(t("adminVendors.toasts.created"));
      }
      setOpenCreate(false);
      fetchList();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminVendors.toasts.failed")));
    }
  };

  const archive = async (v) => {
    try {
      await api.delete(`/admin/vendors/${v.id}`);
      toast.success(t("adminVendors.toasts.archived"));
      fetchList();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminVendors.toasts.failed")));
    }
  };

  // P11-v2 — Two-step promote flow.
  const openPromote = (v) => {
    setPromoteVendor(v);
    setPromoteToken("");
    setPromoteReason("");
    setPromoteTokenExpires(null);
  };
  const closePromote = () => {
    setPromoteVendor(null);
    setPromoteToken("");
    setPromoteReason("");
    setPromoteTokenExpires(null);
    setPromoting(false);
  };
  const issueToken = async () => {
    if (!promoteVendor) return;
    try {
      const r = await api.post(`/admin/vendors/${promoteVendor.id}/promote-real/token`);
      setPromoteToken(r.data?.token || "");
      setPromoteTokenExpires(r.data?.expires_at || null);
      toast.success(t("adminVendors.toasts.tokenIssued"));
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminVendors.toasts.failed")));
    }
  };
  const confirmPromote = async () => {
    if (!promoteVendor || !promoteToken || promoteReason.trim().length < 4) return;
    // Synchronous guard against double-submit.
    if (promotingRef.current) return;
    promotingRef.current = true;
    try {
      setPromoting(true);
      await api.post(`/admin/vendors/${promoteVendor.id}/promote-real`, {
        token: promoteToken,
        reason: promoteReason.trim(),
      });
      toast.success(t("adminVendors.toasts.promoted"));
      closePromote();
      fetchList();
    } catch (err) {
      toast.error(extractErrorMessage(err, t("adminVendors.toasts.failed")));
    } finally {
      setPromoting(false);
      promotingRef.current = false;
    }
  };

  const toggleCategory = (c) => {
    setForm((prev) => {
      const has = (prev.service_categories || []).includes(c);
      return { ...prev, service_categories: has
        ? prev.service_categories.filter((x) => x !== c)
        : [...(prev.service_categories || []), c] };
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-vendors-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold inline-flex items-center gap-2 text-slate-900">
            <Store className="w-6 h-6 text-fuchsia-700" />
            {t("adminVendors.pageTitle")}
          </h1>
          <p className="text-sm text-slate-600 mt-1">{t("adminVendors.pageSub")}</p>
          <p className="text-[11px] text-slate-400 mt-1">{t("adminVendors.privacy")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchList} data-testid="admin-vendors-refresh">
            <RefreshCw className="w-3.5 h-3.5 me-1" /> {t("adminVendors.actions.refresh")}
          </Button>
          <Button onClick={openNew} data-testid="admin-vendors-create">
            <Plus className="w-3.5 h-3.5 me-1" /> {t("adminVendors.create")}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="p-4">
          <label className="block text-xs">
            <span className="block text-slate-600 mb-1">{t("adminVendors.fields.country")}</span>
            <select
              value={filterCountry}
              onChange={(e) => setFilterCountry(e.target.value)}
              className="border rounded px-2 py-1.5 text-sm"
              data-testid="admin-vendors-filter-country"
            >
              <option value="">{t("adminVendors.filters.allCountries") || "All countries"}</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {countryLabel(c.code, t)} ({c.code})
                </option>
              ))}
            </select>
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="admin-vendors-empty">
              {t("adminVendors.empty")}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="admin-vendors-table">
              <thead className="bg-slate-50 text-start text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t("adminVendors.fields.name")}</th>
                  <th className="px-3 py-2">{t("adminVendors.fields.type")}</th>
                  <th className="px-3 py-2">{t("adminVendors.fields.country")}</th>
                  <th className="px-3 py-2">{t("adminVendors.fields.categories")}</th>
                  <th className="px-3 py-2">{t("adminVendors.fields.verified")}</th>
                  <th className="px-3 py-2">{t("adminVendors.fields.active")}</th>
                  <th className="px-3 py-2">Mock / Real</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100 hover:bg-fuchsia-50/40"
                      data-testid={`admin-vendors-row-${v.id}`}>
                    <td className="px-3 py-2 font-medium cursor-pointer" onClick={() => openEdit(v)}>
                      {v.vendor_name}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{v.vendor_type}</td>
                    <td className="px-3 py-2 text-slate-600">{v.country}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{(v.service_categories || []).join(", ")}</td>
                    <td className="px-3 py-2">
                      <Badge className={`${VERIFICATION_PILL[v.verified_status]} border-0 text-[10px] uppercase`}>
                        {t(`adminVendors.verification.${v.verified_status}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs">{v.is_active ? "✓" : "—"}</td>
                    <td className="px-3 py-2">
                      {v.is_mock === false ? (
                        <Badge
                          className="bg-emerald-100 text-emerald-900 border-0 text-[10px] uppercase"
                          data-testid={`admin-vendors-real-badge-${v.id}`}
                        >
                          {t("adminVendors.realBadge")}
                        </Badge>
                      ) : (
                        <Badge
                          className="bg-amber-100 text-amber-900 border-0 text-[10px] uppercase"
                          data-testid={`admin-vendors-mock-badge-${v.id}`}
                        >
                          {t("adminVendors.mockBadge")}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-end whitespace-nowrap">
                      {v.is_mock !== false && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openPromote(v)}
                          data-testid={`admin-vendors-promote-${v.id}`}
                          title={t("adminVendors.promote.button")}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => archive(v)}
                              data-testid={`admin-vendors-archive-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto" data-testid="admin-vendors-drawer">
          <DialogHeader>
            <DialogTitle>{editingId ? form.vendor_name : t("adminVendors.create")}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {t("adminVendors.privacy")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2 text-sm">
            <label className="block">
              <span className="text-slate-600 mb-1 inline-flex items-center gap-1.5">
                {t("adminVendors.fields.name")}
                <RequiredFieldDot title={t("common.requiredField") || "Required field"} />
              </span>
              <Input value={form.vendor_name}
                aria-required="true"
                onChange={(e) => setForm({ ...form, vendor_name: e.target.value })}
                data-testid="admin-vendors-input-name" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-slate-600 mb-1 inline-flex items-center gap-1.5">
                  {t("adminVendors.fields.type")}
                  <RequiredFieldDot />
                </span>
                <select value={form.vendor_type}
                  aria-required="true"
                  onChange={(e) => setForm({ ...form, vendor_type: e.target.value })}
                  className="w-full border rounded px-2 py-1.5"
                  data-testid="admin-vendors-input-type">
                  {VENDOR_TYPES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-slate-600 mb-1 inline-flex items-center gap-1.5">
                  {t("adminVendors.fields.country")}
                  <RequiredFieldDot />
                </span>
                <select value={form.country}
                  aria-required="true"
                  onChange={(e) => {
                    const next = e.target.value;
                    const lang = defaultLangForCountry(next);
                    setForm((f) => ({ ...f, country: next, language: lang || f.language }));
                  }}
                  className="w-full border rounded px-2 py-1.5"
                  data-testid="admin-vendors-input-country">
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {countryLabel(c.code, t)} ({c.code})
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-slate-600 mb-1 block">{t("adminVendors.fields.city")}</span>
                <Input value={form.city || ""}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder={(() => { const v = t("adminVendors.fields.cityPlaceholder"); return v === "adminVendors.fields.cityPlaceholder" ? "Enter city" : v; })()}
                  data-testid="admin-vendors-input-city" />
                <span className="block text-[10px] text-slate-400 mt-0.5" data-testid="admin-vendors-city-hint">
                  {t("adminVendors.fields.cityHint") === "adminVendors.fields.cityHint"
                    ? "City catalog by country is on the backlog — free text for now."
                    : t("adminVendors.fields.cityHint")}
                </span>
              </label>
              <label className="block">
                <span className="text-slate-600 mb-1 block">{t("adminVendors.fields.email")}</span>
                <Input type="email" value={form.contact_email || ""}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  data-testid="admin-vendors-input-email" />
              </label>
            </div>
            <div>
              <span className="text-slate-600 mb-1 block">{t("adminVendors.fields.categories")}</span>
              <div className="flex flex-wrap gap-2">
                {PILOT_CATEGORIES.map((c) => {
                  const active = (form.service_categories || []).includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleCategory(c)}
                      className={`text-xs px-3 py-1 rounded-full border ${active
                        ? "bg-fuchsia-100 border-fuchsia-300 text-fuchsia-900"
                        : "bg-white border-slate-200 text-slate-600"}`}
                      data-testid={`admin-vendors-cat-${c}`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="block">
              <span className="text-slate-600 mb-1 block">{t("adminVendors.fields.verified")}</span>
              <select value={form.verified_status}
                onChange={(e) => setForm({ ...form, verified_status: e.target.value })}
                className="w-full border rounded px-2 py-1.5"
                data-testid="admin-vendors-input-verified">
                {VERIFICATION.map((v) => <option key={v} value={v}>{t(`adminVendors.verification.${v}`)}</option>)}
              </select>
            </label>
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={!!form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                data-testid="admin-vendors-input-active" />
              <span className="text-sm">{t("adminVendors.fields.active")}</span>
            </label>
            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={save} data-testid="admin-vendors-save">
                <ShieldCheck className="w-3.5 h-3.5 me-1" /> {t("adminVendors.actions.save")}
              </Button>
              <Button variant="ghost" onClick={() => setOpenCreate(false)} data-testid="admin-vendors-cancel">
                {t("adminVendors.actions.cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* P11-v2 — Promote to real (two-step). */}
      <Dialog open={!!promoteVendor} onOpenChange={(o) => { if (!o) closePromote(); }}>
        <DialogContent className="sm:max-w-md" data-testid="admin-vendors-promote-dialog">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-600" />
              {t("adminVendors.promote.title")}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500 leading-relaxed">
              {t("adminVendors.promote.description")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {promoteVendor && (
              <div className="text-xs text-slate-700 bg-slate-50 rounded-lg p-2 border border-slate-200">
                <div className="font-medium">{promoteVendor.vendor_name}</div>
                <div className="text-slate-500">{promoteVendor.country} · {promoteVendor.vendor_type}</div>
              </div>
            )}
            {!promoteToken ? (
              <Button
                onClick={issueToken}
                className="w-full"
                data-testid="admin-vendors-promote-issue-token"
              >
                {t("adminVendors.promote.issueToken")}
              </Button>
            ) : (
              <>
                <label className="block">
                  <span className="text-slate-600 mb-1 block text-xs">{t("adminVendors.promote.tokenLabel")}</span>
                  <Input
                    value={promoteToken}
                    onChange={(e) => setPromoteToken(e.target.value)}
                    className="font-mono text-xs"
                    data-testid="admin-vendors-promote-token-input"
                  />
                  {promoteTokenExpires && (
                    <p className="mt-1 text-[10px] text-slate-500">
                      {t("adminVendors.promote.expiresIn")}: {new Date(promoteTokenExpires).toLocaleTimeString()}
                    </p>
                  )}
                </label>
                <label className="block">
                  <span className="text-slate-600 mb-1 block text-xs">{t("adminVendors.promote.reasonLabel")}</span>
                  <Input
                    value={promoteReason}
                    onChange={(e) => setPromoteReason(e.target.value)}
                    placeholder=""
                    minLength={4}
                    data-testid="admin-vendors-promote-reason-input"
                  />
                </label>
                <Button
                  onClick={confirmPromote}
                  disabled={promoting || promoteReason.trim().length < 4 || !promoteToken}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="admin-vendors-promote-confirm"
                >
                  <ShieldCheck className="w-3.5 h-3.5 me-1" />
                  {t("adminVendors.promote.confirm")}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVendors;
